import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { RefreshCw } from 'lucide-react'
import { useEffect } from 'react'

import type { PaymentChannelTab } from '@/modules/bank/type'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { useNavigate } from 'react-router-dom'
import { BankSelector } from '../BankSelector'
import { Separator } from '@/common/ui/separator'
import { Switch } from '@/common/ui/switch'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

const SMILEPAYZ_SCHEMA = {
	paymentGatewayId: true,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: true,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: false,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	defaultChannel: ['QR'] as PaymentChannelTab[],
	notAvailableChannel: ['TRANSFER', 'TRUE_MONEY'] as PaymentChannelTab[],
}

const WELL_PAY_SCHEMA = {
	paymentGatewayId: true,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: true,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	defaultChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
}

const BADOOPAY_SCHEMA = {
	paymentGatewayId: true,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: true,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	defaultChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
}

const TWOSTEPX_SCHEMA = {
	paymentGatewayId: true,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: true,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	defaultChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
}

const GOAL_PAY_SCHEMA = {
	paymentGatewayId: true,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: true,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	defaultChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
}

const TL_PAY_SCHEMA = {
	paymentGatewayId: true,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: true,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	defaultChannel: ['QR'] as PaymentChannelTab[],
	notAvailableChannel: ['TRANSFER', 'TRUE_MONEY'] as PaymentChannelTab[],
}

const G_PAY_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: true,
	paymentGatewayApiKey: false,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	defaultChannel: ['QR'] as PaymentChannelTab[],
	notAvailableChannel: ['TRANSFER', 'TRUE_MONEY'] as PaymentChannelTab[],
}

const ON_PAY_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: false,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: false,
	paymentGatewayAccountNumber: false,
	paymentAccessKey: true,
	defaultChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
}

const PAYONEX_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: false,
	paymentGatewayAccountNumber: false,
	paymentAccessKey: true,
	defaultChannel: ['QR'] as PaymentChannelTab[],
	notAvailableChannel: ['TRANSFER', 'TRUE_MONEY'] as PaymentChannelTab[],
}

const BIBBY_SCHEMA = {
	paymentGatewayId: true,
	paymentGatewaySecretKey: false,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: false,
	paymentAccessKey: false,
	defaultChannel: ['QR'] as PaymentChannelTab[],
	notAvailableChannel: ['TRANSFER', 'TRUE_MONEY'] as PaymentChannelTab[],
}

const TRUE_WALLET_SCHEMA = {
	paymentGatewayId: true,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: true,
	paymentGatewayApiKey: false,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	deviceId: true,
	pin: true,
	paymentGatewayAccountName: true,
	defaultChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
	notAvailableChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
}

const SMS_AUTO_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: true,
	paymentGatewayApiKey: false,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	deviceId: false,
	pin: false,
	senderName: true,
	paymentGatewayAccountName: true,
	paymentGatewayProviderName: true,
	defaultChannel: ['TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['QR', 'TRUE_MONEY'] as PaymentChannelTab[],
}

const SANDSPAY_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: false,
	paymentAccessKey: false,
	defaultChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
}

const TMN_WEBHOOK_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: true,
	paymentGatewayApiKey: false,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	paymentGatewayAccountName: true,
	defaultChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
	notAvailableChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
}

const VERIFY_SLIP_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: false,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: false,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	paymentGatewayAccountName: true,
	paymentGatewayProviderName: true,
	defaultChannel: ['TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['QR', 'TRUE_MONEY'] as PaymentChannelTab[],
}

const AUTOSSLIP_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: false,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: false,
	paymentGatewayAccountNumber: true,
	paymentAccessKey: false,
	paymentGatewayAccountName: true,
	paymentGatewayProviderName: true,
	defaultChannel: ['TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['QR', 'TRUE_MONEY'] as PaymentChannelTab[],
}

const PAYD_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: false,
	paymentAccessKey: false,
	defaultChannel: ['QR'] as PaymentChannelTab[],
	notAvailableChannel: ['TRUE_MONEY', 'TRANSFER'] as PaymentChannelTab[],
}

const MYPAY24_SCHEMA = {
	paymentGatewayId: true,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: false,
	paymentAccessKey: false,
	defaultChannel: ['TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['QR', 'TRUE_MONEY'] as PaymentChannelTab[],
}

const CUBIXPAY_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: true,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: false,
	paymentAccessKey: false,
	defaultChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
}

const AUTOPEER_SCHEMA = {
	paymentGatewayId: false,
	paymentGatewaySecretKey: false,
	paymentGatewayPrivateKey: false,
	paymentGatewayClientId: false,
	paymentGatewayApiKey: true,
	paymentGatewayAccountNumber: false,
	paymentAccessKey: false,
	defaultChannel: ['QR', 'TRANSFER'] as PaymentChannelTab[],
	notAvailableChannel: ['TRUE_MONEY'] as PaymentChannelTab[],
}

/** Schema includes form field visibility (boolean) + channel config for "ช่องทางที่รองรับ" */
type PaymentGatewaySchema = Record<
	string,
	boolean | PaymentChannelTab[] | undefined
>

const SCHEMA_MAPPERS: Record<string, PaymentGatewaySchema> = {
	SMILEPAYZ: SMILEPAYZ_SCHEMA,
	WELLPAY: WELL_PAY_SCHEMA,
	BADOOPAY: BADOOPAY_SCHEMA,
	'2STEPX': TWOSTEPX_SCHEMA,
	TLPAY: TL_PAY_SCHEMA,
	PAYONEX: PAYONEX_SCHEMA,
	GPAY: G_PAY_SCHEMA,
	ONPAY: ON_PAY_SCHEMA,
	BIBBY: BIBBY_SCHEMA,
	TRUE_WALLET: TRUE_WALLET_SCHEMA,
	TMN_WEBHOOK: TMN_WEBHOOK_SCHEMA,
	SMS_AUTO: SMS_AUTO_SCHEMA,
	SANDSPAY: SANDSPAY_SCHEMA,
	GOALPAY: GOAL_PAY_SCHEMA,
	VERIFY_SLIP: VERIFY_SLIP_SCHEMA,
	AUTOSSLIP: AUTOSSLIP_SCHEMA,
	PAYD: PAYD_SCHEMA,
	MYPAY24: MYPAY24_SCHEMA,
	CUBIXPAY: CUBIXPAY_SCHEMA,
	AUTOPEER: AUTOPEER_SCHEMA,
}

export function getChannelConfig(provider: string): {
	defaultChannel: PaymentChannelTab[]
	notAvailableChannel: PaymentChannelTab[]
} {
	const schema = SCHEMA_MAPPERS[provider]
	if (!schema || !Array.isArray(schema.defaultChannel))
		return {
			defaultChannel: ['QR', 'TRANSFER'],
			notAvailableChannel: [],
		}
	return {
		defaultChannel: schema.defaultChannel,
		notAvailableChannel: Array.isArray(schema.notAvailableChannel)
			? schema.notAvailableChannel
			: [],
	}
}

// Label mappers for custom field labels
const TRUE_WALLET_LABELS = {
	paymentGatewayId: 'Key id',
	paymentGatewaySecretKey: 'Login token',
	paymentGatewayClientId: 'Client Id',
	paymentGatewayAccountNumber: 'Phone number',
	deviceId: 'Device Id',
	pin: 'Pin รหัส 6 ตัว',
	paymentGatewayAccountName: 'ชื่อบัญชี',
}

const TMN_WEBHOOK_LABELS = {
	paymentGatewayClientId: 'Client ID',
	paymentGatewaySecretKey: 'Client Secret',
	paymentGatewayAccountNumber: 'เบอร์โทรศัพท์ (บัญชีรับเงิน)',
	paymentGatewayAccountName: 'ชื่อบัญชี',
}

const MYPAY24_LABELS = {
	paymentGatewayId: 'Merchant Username',
	paymentGatewayApiKey: 'API Key',
	paymentGatewaySecretKey: 'Secret Key',
}

const CUBIXPAY_LABELS = {
	paymentGatewayApiKey: 'API Key',
	paymentGatewaySecretKey: 'API Secret',
}

const AUTOPEER_LABELS = {
	paymentGatewayApiKey: 'API Key (x-atp-key)',
}

const SMS_AUTO_LABELS = {
	paymentGatewayClientId: 'Client ID',
	paymentGatewaySecretKey: 'Client Secret',
	senderName: 'ชื่อผู้ส่ง',
	paymentGatewayAccountName: 'ชื่อบัญชี',
	paymentGatewayProviderName: 'ธนาคาร',
	paymentGatewayAccountNumber: 'เลขที่บัญชี',
}

type FieldLabelKey =
	| keyof typeof TRUE_WALLET_LABELS
	| keyof typeof SMS_AUTO_LABELS
	| keyof typeof TMN_WEBHOOK_LABELS
	| keyof typeof MYPAY24_LABELS
	| keyof typeof CUBIXPAY_LABELS
	| keyof typeof AUTOPEER_LABELS

const getFieldLabel = (
	paymentGatewayProvider: string,
	fieldName: FieldLabelKey,
	defaultLabel: string,
): string => {
	if (
		paymentGatewayProvider === 'TRUE_WALLET' &&
		fieldName in TRUE_WALLET_LABELS
	) {
		return TRUE_WALLET_LABELS[fieldName as keyof typeof TRUE_WALLET_LABELS]
	}
	if (paymentGatewayProvider === 'SMS_AUTO' && fieldName in SMS_AUTO_LABELS) {
		return SMS_AUTO_LABELS[fieldName as keyof typeof SMS_AUTO_LABELS]
	}
	if (
		paymentGatewayProvider === 'TMN_WEBHOOK' &&
		fieldName in TMN_WEBHOOK_LABELS
	) {
		return TMN_WEBHOOK_LABELS[fieldName as keyof typeof TMN_WEBHOOK_LABELS]
	}
	if (paymentGatewayProvider === 'MYPAY24' && fieldName in MYPAY24_LABELS) {
		return MYPAY24_LABELS[fieldName as keyof typeof MYPAY24_LABELS]
	}
	if (paymentGatewayProvider === 'CUBIXPAY' && fieldName in CUBIXPAY_LABELS) {
		return CUBIXPAY_LABELS[fieldName as keyof typeof CUBIXPAY_LABELS]
	}
	if (paymentGatewayProvider === 'AUTOPEER' && fieldName in AUTOPEER_LABELS) {
		return AUTOPEER_LABELS[fieldName as keyof typeof AUTOPEER_LABELS]
	}
	return defaultLabel
}

type ConstraintsConfig = {
	providerMinDeposit: number
	globalMaxDeposit: number
}

const formSchema = (
	schema: PaymentGatewaySchema,
	provider: string,
	constraints: ConstraintsConfig,
) =>
	z
		.object({
			displayName: schema.displayName
				? z.string().min(1, 'กรุณากรอกชื่อ')
				: z.string().optional(),
			bankProviderName: schema.bankProviderName
				? z.string().min(1, 'กรุณากรอกชื่อผู้ให้บริการ')
				: z.string().optional(),
			paymentGatewayId: schema.paymentGatewayId
				? z.string().min(1, 'กรุณากรอก Payment Gateway ID')
				: z.string().optional(),
			paymentGatewaySecretKey: schema.paymentGatewaySecretKey
				? z.string().min(1, 'กรุณากรอก Secret Key')
				: z.string().optional(),
			paymentGatewayPrivateKey: schema.paymentGatewayPrivateKey
				? z.string().min(1, 'กรุณากรอก Secret Key')
				: z.string().optional(),
			paymentGatewayClientId: schema.paymentGatewayClientId
				? z.string().min(1, 'กรุณากรอก Client ID')
				: z.string().optional(),
			paymentGatewayApiKey: schema.paymentGatewayApiKey
				? z.string().min(1, 'กรุณากรอก API Key')
				: z.string().optional(),
			paymentGatewayAccountNumber:
				schema.paymentGatewayAccountNumber && provider === 'TRUE_WALLET'
					? z.string().min(1, 'กรุณากรอกหมายเลขโทรศัพท์')
					: schema.paymentGatewayAccountNumber && provider === 'SMS_AUTO'
						? z.string().min(1, 'กรุณากรอกเลขที่บัญชี')
						: z.string().optional(),
			paymentAccessKey: schema.paymentAccessKey
				? z.string().min(1, 'กรุณากรอก Payment Access Key')
				: z.string().optional(),
			deviceId: schema.deviceId
				? z.string().min(1, 'กรุณากรอก Device ID')
				: z.string().optional(),
			pin: schema.pin
				? z.string().min(6, 'PIN ต้องมี 6 ตัว').max(6, 'PIN ต้องมี 6 ตัว')
				: z.string().optional(),
			senderName: schema.senderName
				? z.string().min(1, 'กรุณากรอก Sender Name')
				: z.string().optional(),
			paymentGatewayAccountName:
				schema.paymentGatewayAccountName &&
				provider !== 'TRUE_WALLET' &&
				provider !== 'TMN_WEBHOOK'
					? z.string().min(1, 'กรุณากรอกชื่อบัญชี')
					: z.string().optional(),
			paymentGatewayProviderName: schema.paymentGatewayProviderName
				? z.string().min(1, 'กรุณากรอกธนาคาร')
				: z.string().optional(),
			minDepositAmount: z.coerce
				.number()
				.min(
					constraints.providerMinDeposit,
					`จำนวนเงินฝากขั้นต่ำต้องไม่น้อยกว่า ${constraints.providerMinDeposit.toLocaleString()} บาท`,
				),
			maxDepositAmount: z.coerce
				.number()
				.max(
					constraints.globalMaxDeposit,
					`จำนวนเงินฝากสูงสุดต้องไม่เกิน ${constraints.globalMaxDeposit.toLocaleString()} บาท`,
				),
			enableUploadSlip: z.boolean().optional(),
			uploadSlipDelaySeconds: z.coerce
				.number()
				.min(0, 'ต้องไม่น้อยกว่า 0 วินาที')
				.optional(),
			isP2p: z.boolean().optional(),
			p2pStep: z.coerce.number().min(1, 'ต้องมากกว่า 0').optional(),
			p2pThreshold: z.coerce.number().min(1).max(100).optional(),
		})
		.refine((data) => data.minDepositAmount <= data.maxDepositAmount, {
			message: 'จำนวนเงินฝากขั้นต่ำต้องไม่มากกว่าจำนวนเงินฝากสูงสุด',
			path: ['minDepositAmount'],
		})

export type FormValues = z.infer<ReturnType<typeof formSchema>>

/**
 * Generates a random string of specified length using A-Z and a-z characters
 */
const generateRandomString = (length: number): string => {
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
	let result = ''
	for (let i = 0; i < length; i++) {
		result += chars.charAt(Math.floor(Math.random() * chars.length))
	}
	return result
}

export type PaymentGatewayFormDefaultValues = {
	displayName?: string
	paymentGatewayId?: string
	paymentGatewaySecretKey?: string
	paymentGatewayPrivateKey?: string
	paymentGatewayClientId?: string
	paymentGatewayApiKey?: string
	paymentGatewayAccountNumber?: string
	deviceId?: string
	pin?: string
	senderName?: string
	paymentGatewayAccountName?: string
	paymentGatewayProviderName?: string
	minDepositAmount?: number
	maxDepositAmount?: number
	enableUploadSlip?: boolean
	uploadSlipDelaySeconds?: number
	isP2p?: boolean
	p2pStep?: number
	p2pThreshold?: number
}

interface Props {
	paymentGatewayProvider: string
	defaultValues?: PaymentGatewayFormDefaultValues
	constraints: ConstraintsConfig
	onSubmit: (values: {
		displayName: string
		bankProviderName: string
		paymentGatewayId: string
		paymentGatewaySecretKey: string
		paymentGatewayPrivateKey: string
		paymentGatewayClientId: string
		paymentGatewayApiKey: string
		paymentGatewayAccountNumber: string
		deviceId?: string
		pin?: string
		senderName?: string
		paymentGatewayAccountName?: string
		paymentGatewayProviderName?: string
		minDepositAmount: number
		maxDepositAmount: number
		enableUploadSlip?: boolean
		uploadSlipDelaySeconds?: number
		isP2p?: boolean
		p2pStep?: number
		p2pThreshold?: number
	}) => void
	mode: 'create' | 'edit'
	serverFieldErrors?: Record<string, string>
}

export default function PaymentGatewayForm({
	paymentGatewayProvider,
	defaultValues,
	constraints,
	onSubmit,
	mode,
	serverFieldErrors,
}: Props) {
	const navigate = useNavigate()

	// Auto-generate Client Id and Client Secret for SMS_AUTO provider in create mode
	const shouldAutoGenerateClientId =
		mode === 'create' &&
		(paymentGatewayProvider === 'SMS_AUTO' ||
			paymentGatewayProvider === 'TMN_WEBHOOK')

	const shouldAutoGenerateClientSecret =
		mode === 'create' && paymentGatewayProvider === 'SMS_AUTO'

	const form = useForm<FormValues>({
		resolver: zodResolver(
			formSchema(
				SCHEMA_MAPPERS[paymentGatewayProvider],
				paymentGatewayProvider,
				constraints,
			),
		),
		defaultValues: {
			bankProviderName: paymentGatewayProvider,
			displayName: defaultValues?.displayName || '',
			paymentGatewayId: defaultValues?.paymentGatewayId || '',
			paymentGatewaySecretKey:
				defaultValues?.paymentGatewaySecretKey ||
				(shouldAutoGenerateClientSecret ? generateRandomString(30) : ''),
			paymentGatewayPrivateKey: defaultValues?.paymentGatewayPrivateKey || '',
			paymentGatewayClientId:
				defaultValues?.paymentGatewayClientId ||
				(shouldAutoGenerateClientId ? generateRandomString(30) : ''),
			paymentGatewayApiKey: defaultValues?.paymentGatewayApiKey || '',
			paymentAccessKey:
				paymentGatewayProvider === 'ONPAY' ||
				paymentGatewayProvider === 'PAYONEX'
					? defaultValues?.paymentGatewayClientId ||
						defaultValues?.paymentGatewayId
					: '',
			deviceId: defaultValues?.deviceId || '',
			pin: defaultValues?.pin || '',
			senderName: defaultValues?.senderName || '',
			paymentGatewayAccountNumber:
				defaultValues?.paymentGatewayAccountNumber || '',
			paymentGatewayAccountName: defaultValues?.paymentGatewayAccountName || '',
			paymentGatewayProviderName:
				defaultValues?.paymentGatewayProviderName || '',
			minDepositAmount:
				defaultValues?.minDepositAmount ?? constraints.providerMinDeposit,
			maxDepositAmount:
				defaultValues?.maxDepositAmount ?? constraints.globalMaxDeposit,
			enableUploadSlip:
				defaultValues?.enableUploadSlip ??
				(paymentGatewayProvider === 'MYPAY24'
					? true
					: paymentGatewayProvider === 'CUBIXPAY'
						? true
						: paymentGatewayProvider === 'AUTOPEER'
							? true
							: paymentGatewayProvider === 'AUTOSSLIP'
								? true
								: false),
			uploadSlipDelaySeconds:
				defaultValues?.uploadSlipDelaySeconds ??
				(paymentGatewayProvider === 'MYPAY24'
					? 0
					: paymentGatewayProvider === 'CUBIXPAY'
						? 30
						: paymentGatewayProvider === 'AUTOPEER'
							? 30
							: 60),
			isP2p: defaultValues?.isP2p ?? false,
			p2pStep: defaultValues?.p2pStep ?? 1,
			p2pThreshold: defaultValues?.p2pThreshold ?? undefined,
		},
	})

	useEffect(() => {
		if (serverFieldErrors && Object.keys(serverFieldErrors).length > 0) {
			Object.entries(serverFieldErrors).forEach(([field, message]) => {
				form.setError(field as keyof FormValues, {
					type: 'server',
					message,
				})
			})
		}
	}, [serverFieldErrors, form])

	const enableUploadSlipValue = form.watch('enableUploadSlip')

	const handleSubmit = (values: FormValues) => {
		if (
			values.bankProviderName === 'ONPAY' ||
			values.bankProviderName === 'PAYONEX'
		) {
			onSubmit({
				displayName: values.displayName || '',
				bankProviderName: values.bankProviderName || '',
				paymentGatewayId: values.paymentAccessKey || '',
				paymentGatewaySecretKey: values.paymentGatewaySecretKey || '',
				paymentGatewayPrivateKey: values.paymentGatewayPrivateKey || '',
				paymentGatewayClientId: values.paymentAccessKey || '',
				paymentGatewayApiKey: values.paymentGatewayApiKey || '',
				paymentGatewayAccountNumber: values.paymentGatewayAccountNumber || '',
				minDepositAmount: values.minDepositAmount,
				maxDepositAmount: values.maxDepositAmount,
				isP2p: values.isP2p,
				p2pStep: values.isP2p ? values.p2pStep : undefined,
			})

			return
		}

		if (values.bankProviderName === 'SANDSPAY') {
			onSubmit({
				displayName: values.displayName || '',
				bankProviderName: values.bankProviderName || '',
				paymentGatewayId: values.paymentGatewayApiKey || '',
				paymentGatewaySecretKey: values.paymentGatewaySecretKey || '',
				paymentGatewayPrivateKey: values.paymentGatewayPrivateKey || '',
				paymentGatewayClientId: values.paymentGatewayClientId || '',
				paymentGatewayApiKey: values.paymentGatewayApiKey || '',
				paymentGatewayAccountNumber: values.paymentGatewayAccountNumber || '',
				minDepositAmount: values.minDepositAmount,
				maxDepositAmount: values.maxDepositAmount,
				isP2p: values.isP2p,
				p2pStep: values.isP2p ? values.p2pStep : undefined,
			})

			return
		}

		onSubmit({
			displayName: values.displayName || '',
			bankProviderName: values.bankProviderName || '',
			paymentGatewayId: values.paymentGatewayId || '',
			paymentGatewaySecretKey: values.paymentGatewaySecretKey || '',
			paymentGatewayPrivateKey: values.paymentGatewayPrivateKey || '',
			paymentGatewayClientId: values.paymentGatewayClientId || '',
			paymentGatewayApiKey: values.paymentGatewayApiKey || '',
			paymentGatewayAccountNumber: values.paymentGatewayAccountNumber || '',
			deviceId: values.deviceId || '',
			pin: values.pin || '',
			senderName: values.senderName || '',
			paymentGatewayAccountName: values.paymentGatewayAccountName || '',
			paymentGatewayProviderName: values.paymentGatewayProviderName || '',
			minDepositAmount: values.minDepositAmount,
			maxDepositAmount: values.maxDepositAmount,
			enableUploadSlip: values.enableUploadSlip,
			uploadSlipDelaySeconds: values.enableUploadSlip
				? (values.uploadSlipDelaySeconds ?? 60)
				: undefined,
			isP2p: values.isP2p,
			p2pStep: values.isP2p ? values.p2pStep : undefined,
			p2pThreshold: values.isP2p ? values.p2pThreshold : undefined,
		})
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleSubmit, showFormValidationToast)}
				className="space-y-8"
			>
				<div className="max-w-md space-y-8">
					<FormField
						control={form.control}
						name="displayName"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>Display Name</FormLabel>
								<FormControl>
									<Input placeholder="กรอก Display Name" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{SCHEMA_MAPPERS[paymentGatewayProvider].paymentGatewayId && (
						<FormField
							control={form.control}
							name="paymentGatewayId"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{getFieldLabel(
											paymentGatewayProvider,
											'paymentGatewayId',
											'Payment Gateway ID',
										)}
									</FormLabel>
									<FormControl>
										<Input
											placeholder={`กรอก ${getFieldLabel(
												paymentGatewayProvider,
												'paymentGatewayId',
												'Payment Gateway ID',
											)}`}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider].paymentGatewayClientId && (
						<FormField
							control={form.control}
							name="paymentGatewayClientId"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{getFieldLabel(
											paymentGatewayProvider,
											'paymentGatewayClientId',
											'Client ID',
										)}
									</FormLabel>
									<FormControl>
										<div className="flex gap-2">
											<Input
												placeholder={`กรอก ${getFieldLabel(
													paymentGatewayProvider,
													'paymentGatewayClientId',
													'Client ID',
												)}`}
												{...field}
											/>
											{(paymentGatewayProvider === 'SMS_AUTO' ||
												paymentGatewayProvider === 'TMN_WEBHOOK') && (
												<Button
													type="button"
													variant="outline"
													size="icon"
													onClick={() => {
														const newId = generateRandomString(30)
														form.setValue('paymentGatewayClientId', newId)
													}}
													title="สร้างใหม่"
												>
													<RefreshCw className="h-4 w-4" />
												</Button>
											)}
										</div>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider].paymentGatewaySecretKey && (
						<FormField
							control={form.control}
							name="paymentGatewaySecretKey"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{getFieldLabel(
											paymentGatewayProvider,
											'paymentGatewaySecretKey',
											'Secret Key',
										)}
									</FormLabel>
									<FormControl>
										<div className="flex gap-2">
											<Input
												placeholder={`กรอก ${getFieldLabel(
													paymentGatewayProvider,
													'paymentGatewaySecretKey',
													'Secret Key',
												)}`}
												{...field}
											/>
											{paymentGatewayProvider === 'SMS_AUTO' && (
												<Button
													type="button"
													variant="outline"
													size="icon"
													onClick={() => {
														const newKey = generateRandomString(30)
														form.setValue('paymentGatewaySecretKey', newKey)
													}}
													title="สร้างใหม่"
												>
													<RefreshCw className="h-4 w-4" />
												</Button>
											)}
										</div>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider].paymentGatewayPrivateKey && (
						<FormField
							control={form.control}
							name="paymentGatewayPrivateKey"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>Private Key</FormLabel>
									<FormControl>
										<Input placeholder="กรอก Private Key" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider].paymentGatewayApiKey && (
						<FormField
							control={form.control}
							name="paymentGatewayApiKey"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>API Key</FormLabel>
									<FormControl>
										<Input placeholder="กรอก API Key" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider].paymentAccessKey && (
						<FormField
							control={form.control}
							name="paymentAccessKey"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>Payment Access Key</FormLabel>
									<FormControl>
										<Input placeholder="กรอก Payment Access Key" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider].deviceId && (
						<FormField
							control={form.control}
							name="deviceId"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{getFieldLabel(
											paymentGatewayProvider,
											'deviceId',
											'Device ID',
										)}
									</FormLabel>
									<FormControl>
										<Input
											placeholder={`กรอก ${getFieldLabel(
												paymentGatewayProvider,
												'deviceId',
												'Device ID',
											)}`}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider].pin && (
						<FormField
							control={form.control}
							name="pin"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{getFieldLabel(paymentGatewayProvider, 'pin', 'PIN')}
									</FormLabel>
									<FormControl>
										<Input
											maxLength={6}
											minLength={6}
											placeholder={`กรอก ${getFieldLabel(
												paymentGatewayProvider,
												'pin',
												'PIN',
											)}`}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider].paymentGatewayAccountName && (
						<FormField
							control={form.control}
							name="paymentGatewayAccountName"
							render={({ field }) => (
								<FormItem>
									<FormLabel
										required={
											paymentGatewayProvider === 'SMS_AUTO' ||
											paymentGatewayProvider === 'VERIFY_SLIP'
										}
									>
										{getFieldLabel(
											paymentGatewayProvider,
											'paymentGatewayAccountName',
											'ชื่อบัญชี',
										)}
									</FormLabel>
									<FormControl>
										<Input
											placeholder={`กรอก ${getFieldLabel(
												paymentGatewayProvider,
												'paymentGatewayAccountName',
												'ชื่อบัญชี',
											)}`}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider]
						.paymentGatewayAccountNumber && (
						<FormField
							control={form.control}
							name="paymentGatewayAccountNumber"
							render={({ field }) => (
								<FormItem>
									<FormLabel
										required={
											paymentGatewayProvider === 'TRUE_WALLET' ||
											paymentGatewayProvider === 'SMS_AUTO'
										}
									>
										{getFieldLabel(
											paymentGatewayProvider,
											'paymentGatewayAccountNumber',
											'Bank Account',
										)}
									</FormLabel>
									<FormControl>
										<Input
											placeholder={`กรอก ${getFieldLabel(
												paymentGatewayProvider,
												'paymentGatewayAccountNumber',
												'Bank Account',
											)}`}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider]
						.paymentGatewayProviderName && (
						<BankSelector
							control={form.control}
							label={getFieldLabel(
								paymentGatewayProvider,
								'paymentGatewayProviderName',
								'ธนาคาร',
							)}
							name="paymentGatewayProviderName"
							required={true}
						/>
					)}

					{SCHEMA_MAPPERS[paymentGatewayProvider].senderName && (
						<FormField
							control={form.control}
							name="senderName"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{getFieldLabel(
											paymentGatewayProvider,
											'senderName',
											'Sender Name',
										)}
									</FormLabel>
									<FormControl>
										<Input
											placeholder={`กรอก ${getFieldLabel(
												paymentGatewayProvider,
												'senderName',
												'Sender Name',
											)}`}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					<Separator />

					<div className="space-y-4">
						<h3 className="text-lg font-medium">ตั้งค่าช่วงเงินฝาก</h3>
						<p className="text-sm text-muted-foreground">
							กำหนดช่วงจำนวนเงินฝากที่รองรับสำหรับธนาคารออโต้นี้
						</p>

						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="minDepositAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>จำนวนเงินฝากขั้นต่ำ (บาท)</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="0"
												{...field}
												onChange={(e) => field.onChange(e.target.valueAsNumber)}
											/>
										</FormControl>
										<FormDescription>
											ขั้นต่ำ: {constraints.providerMinDeposit.toLocaleString()}{' '}
											บาท
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="maxDepositAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>จำนวนเงินฝากสูงสุด (บาท)</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="0"
												{...field}
												onChange={(e) => field.onChange(e.target.valueAsNumber)}
											/>
										</FormControl>
										<FormDescription>
											สูงสุด: {constraints.globalMaxDeposit.toLocaleString()}{' '}
											บาท
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{(paymentGatewayProvider === 'MYPAY24' ||
						paymentGatewayProvider === 'AUTOPEER') && (
						<>
							<Separator />
							<div className="space-y-4">
								<h3 className="text-lg font-medium">ตั้งค่า P2P</h3>
								<p className="text-sm text-muted-foreground">
									กำหนดค่า P2P สำหรับ Payment Gateway นี้
								</p>

								<FormField
									control={form.control}
									name="p2pStep"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>ขั้นต่ำการเพิ่มยอด (Gap)</FormLabel>
											<FormControl>
												<Input
													type="number"
													min={1}
													placeholder="1"
													{...field}
													onChange={(e) =>
														field.onChange(e.target.valueAsNumber)
													}
												/>
											</FormControl>
											<FormDescription>
												จำนวนเงินฝากต้องเพิ่มขึ้นครั้งละเท่าไร (เช่น 50 = 50,
												100, 150...)
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="p2pThreshold"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Threshold แนะนำ (%)</FormLabel>
											<FormControl>
												<Input
													type="number"
													min={1}
													max={100}
													placeholder="50"
													{...field}
													value={field.value ?? ''}
													onChange={(e) =>
														field.onChange(
															e.target.value
																? e.target.valueAsNumber
																: undefined,
														)
													}
												/>
											</FormControl>
											<FormDescription>
												แนะนำยอดฝากใกล้เคียงไม่เกิน % จากยอดที่ผู้เล่นระบุ (เช่น
												50% = ฝาก 50 แนะนำไม่เกิน 75)
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</>
					)}

					{(paymentGatewayProvider === 'SMS_AUTO' ||
						paymentGatewayProvider === 'MYPAY24' ||
						paymentGatewayProvider === 'CUBIXPAY' ||
						paymentGatewayProvider === 'AUTOPEER' ||
						paymentGatewayProvider === 'AUTOSSLIP') && (
						<>
							<Separator />
							<FormField
								control={form.control}
								name="enableUploadSlip"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
										<div className="space-y-0.5">
											<FormLabel className="text-base">
												เปิดใช้การอัปโหลดสลิป
											</FormLabel>
											<FormDescription>
												เมื่อเปิด
												ผู้เล่นสามารถอัปโหลดสลิปเพื่อยืนยันรายการฝากได้ตามเวลาที่กำหนด
											</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={field.value ?? false}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							{enableUploadSlipValue && (
								<FormField
									control={form.control}
									name="uploadSlipDelaySeconds"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												ผู้เล่นสามารถอัปโหลดสลิปได้หลังจากสร้างรายการฝาก
											</FormLabel>
											<div className="flex items-center gap-2">
												<FormControl>
													<Input
														type="number"
														min={0}
														placeholder="60"
														className="w-32"
														{...field}
														onChange={(e) =>
															field.onChange(e.target.valueAsNumber)
														}
													/>
												</FormControl>
												<span className="text-sm text-muted-foreground">
													วินาที
												</span>
											</div>
											<FormDescription>
												ค่าเริ่มต้น: 60 วินาที (1 นาที)
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</>
					)}

					<div className="flex gap-4">
						<Button type="submit" variant="default">
							{mode === 'create' ? 'สร้างธนาคารออโต้' : 'แก้ไขธนาคารออโต้'}
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => navigate(-1)}
						>
							ยกเลิก
						</Button>
					</div>
				</div>
			</form>
		</Form>
	)
}
